import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, BarChart3, Filter, Download, CheckCircle, Settings, ChevronDown, History, TrendingUp, Target, Users, Zap } from 'lucide-react';
import { Project, CompanySummary, ProjectStatus, KickerEligiblePerson, PerformanceKicker, TacticalSpecialist } from '@/types/project';
import { Client, countsTowardMRR } from '@/types/crm';
import { resolveMonthlyData, migrateProjectToTemplate } from '@/utils/projectUtils';
import { exportAndDownloadProjects } from '@/utils/csvExport';
import { MarkAsPaidDialog } from '@features/payments/components/MarkAsPaidDialog';
import { DeleteProjectDialog } from './DeleteProjectDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useOwnerSettings } from '@/contexts/OwnerSettingsContext';
import { projectAPI } from '@/utils/supabase/client';

interface ProjectManagerProps {
  projects: Project[];
  onSelectProject: (project: Project | null) => void;
  onDeleteProject: (projectId: string) => void;
  selectedProject: Project | null;
  onRefresh?: () => void;
  onViewPayments?: () => void;
  isAdmin?: boolean;
}

const STRATEGY_PARTNERS = ['Ken', 'Sean', 'Tim', 'Trav', 'Chase'];

export function ProjectManager({ projects, onSelectProject, onDeleteProject, selectedProject, onRefresh, onViewPayments }: ProjectManagerProps) {
  const { canMarkAsPaid, canDeleteProjects, canEditProjects } = usePermissions();
  const { disabledOwners, setDisabledOwners } = useOwnerSettings();

  const [showCompanySummary, setShowCompanySummary] = useState(true);
  const [showOwnerSettings, setShowOwnerSettings] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('active');
  const [includeProspective, setIncludeProspective] = useState(false);
  const [includeProjectedKickers, setIncludeProjectedKickers] = useState(true);
  const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [showWeightedPipeline, setShowWeightedPipeline] = useState(true);
  const [pipelineClients, setPipelineClients] = useState<Client[]>([]);

  // Load clients for weighted pipeline
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clients = await projectAPI.getClients({ excludeChurned: true });
        setPipelineClients(clients);
      } catch (error) {
        console.error('Failed to load pipeline clients:', error);
      }
    };
    loadClients();
  }, []);

  // Calculate pipeline metrics
  const calculatePipelineMetrics = () => {
    const activeClients = pipelineClients.filter(c => c.lifecycle_stage === 'active');
    const committedClients = pipelineClients.filter(c => c.lifecycle_stage === 'committed');
    const opportunityClients = pipelineClients.filter(c => ['lead', 'opportunity', 'interest', 'deck_sent'].includes(c.lifecycle_stage));

    const clientValue = (c: Client) => c.confirmed_monthly_revenue || c.estimated_monthly_value || 0;

    // One-off deals are single payments — held out of every MRR figure and
    // surfaced on their own line instead.
    const recurringOnly = (list: Client[]) => list.filter(c => countsTowardMRR(c.revenue_type));

    const activeRevenue = recurringOnly(activeClients).reduce((sum, c) => sum + clientValue(c), 0);

    const committedRevenue = recurringOnly(committedClients).reduce((sum, c) => sum + clientValue(c), 0);

    const pipelineTotal = recurringOnly(opportunityClients).reduce((sum, c) =>
      sum + (c.estimated_monthly_value || 0), 0
    );

    const oneOffClients = [...activeClients, ...committedClients, ...opportunityClients]
      .filter(c => !countsTowardMRR(c.revenue_type));
    const oneOffTotal = oneOffClients.reduce((sum, c) => sum + clientValue(c), 0);

    const totalForecast = activeRevenue + committedRevenue + pipelineTotal;

    return {
      activeRevenue,
      activeCount: activeClients.length,
      committedRevenue,
      committedCount: committedClients.length,
      pipelineTotal,
      pipelineCount: opportunityClients.length,
      oneOffTotal,
      oneOffCount: oneOffClients.length,
      totalForecast,
    };
  };

  const pipelineMetrics = calculatePipelineMetrics();

  // Get current month for payment tracking
  const currentMonth = new Date().toISOString().substring(0, 7);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // For variable projects, use current month's ad-hoc revenue; for recurring, use base revenue
  const getProjectRevenue = (project: Project, baseRevenue: number): number => {
    if (project.revenueType === 'variable') {
      const forecast = project.adHocForecasts?.find(f => f.month === currentMonth);
      return forecast?.amount || 0;
    }
    return baseRevenue;
  };

  const calculateCompanySummary = (): CompanySummary => {
    console.log('=== COMPANY SUMMARY CALCULATION START ===');
    console.log('Using BASE TEMPLATE DATA ONLY - NO MONTHLY OVERRIDES');
    console.log(`Include Projected Kickers: ${includeProjectedKickers}`);
    if (disabledOwners.length > 0) {
      console.log(`Disabled Owners: ${disabledOwners.join(', ')} - profit will redistribute to ${STRATEGY_PARTNERS.filter(o => !disabledOwners.includes(o)).join(', ')}`);
    }
    console.log('===========================================');
    
    let totalMonthlyRevenue = 0;
    let totalProfit = 0;
    let totalOperatingCosts = 0;
    let totalKickerRevenue = 0;
    const ownerDistributions: Record<string, number> = {};
    const ownerOperationsEarnings: Record<string, number> = {};
    const ownerTacticalEarnings: Record<string, number> = {};
    const ownerKickerEarnings: Record<string, number> = {};
    // Track specialist vs owner share separately for UI breakdown
    const ownerKickerSpecialistEarnings: Record<string, number> = {};
    const ownerKickerOwnerShare: Record<string, number> = {};

    // Cost breakdowns
    let totalTacticalCosts = 0;
    let totalOperationsPercentageCosts = 0;
    let totalOperationsCompensationCosts = 0;
    let totalKickerCosts = 0;

    // Initialize owner distributions, tactical earnings, operations earnings, and kicker earnings
    STRATEGY_PARTNERS.forEach(owner => {
      ownerDistributions[owner] = 0;
      ownerTacticalEarnings[owner] = 0;
      ownerOperationsEarnings[owner] = 0;
      ownerKickerEarnings[owner] = 0;
      ownerKickerSpecialistEarnings[owner] = 0;
      ownerKickerOwnerShare[owner] = 0;
    });

    // Company summary uses base template data only - no kickers or monthly overrides
    // Filter projects based on status (active projects + optionally prospective)
    const projectsToInclude = projects.filter(project => {
      if (project.status === 'inactive') return false;
      if (project.revenueType === 'variable' && (!project.adHocForecasts || project.adHocForecasts.length === 0)) return false;
      if (project.status === 'prospective') return includeProspective;
      return true; // active projects always included
    });

    projectsToInclude.forEach(project => {
      // Migrate old format if needed
      const migratedProject = migrateProjectToTemplate(project);
      
      // COMPANY SUMMARY USES ONLY BASE TEMPLATE DATA - NO MONTHLY OVERRIDES
      if (!migratedProject.baseTemplate) return; // Skip if no base template
      
      const baseTemplateData = migratedProject.baseTemplate;
      const revenue = getProjectRevenue(project, baseTemplateData.revenue);
      const profitMarginDecimal = baseTemplateData.profitMargin / 100;

      console.log(`=== COMPANY SUMMARY CALCULATION DEBUG ===`);
      console.log(`Project: ${project.projectName || 'Unnamed'}`);
      console.log(`Using BASE TEMPLATE ONLY (no monthly overrides)`);
      console.log(`Base revenue: ${revenue}`);
      console.log(`Base profit margin: ${baseTemplateData.profitMargin}%`);
      console.log(`Include kickers: ${includeProjectedKickers}`);
      console.log(`=========================================`);
      
      // === COMPANY SUMMARY CALCULATION LOGIC (BASE TEMPLATE ONLY) ===
      
      // 1. Calculate operations costs (percentage of revenue + fixed compensation)
      const operationsCompensationPerOwner = baseTemplateData.operationsCompensationPerOwner || 2500;
      const totalOperationsCompensation = baseTemplateData.operationsOwners.length * operationsCompensationPerOwner;
      const baseOperationsCost = revenue * (baseTemplateData.operationsOverheadPercentage / 100);
      const operationsCost = baseOperationsCost + totalOperationsCompensation;
      
      // 2. Calculate tactical specialist costs (base amounts only)
      const tacticalSpecialistsCosts = baseTemplateData.tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);
      
      // 3. Calculate kicker revenue and costs if enabled
      let projectKickerRevenue = 0;
      let projectKickerCosts = 0;
      
      if (includeProjectedKickers && baseTemplateData.performanceKickers && baseTemplateData.performanceKickers.length > 0) {
        const KICKER_TIERS = [
          { value: 'none', label: 'No Performance Bonus', percentage: 0, description: 'Below 5.0X ROAS' },
          { value: 'tier1', label: '5% Kicker', percentage: 0.05, description: '5.0-5.9X ROAS' },
          { value: 'tier2', label: '7.5% Kicker', percentage: 0.075, description: '6.0-6.9X ROAS' },
          { value: 'tier3', label: '10% Kicker', percentage: 0.10, description: '7.0X+ ROAS' },
        ];

        // Helper to resolve eligible people from new or legacy format
        const resolveKickerEligiblePeople = (kicker: PerformanceKicker, specialists: TacticalSpecialist[], operationsOwners: string[]): KickerEligiblePerson[] => {
          // Use new format if available
          if (kicker.eligiblePeople && kicker.eligiblePeople.length > 0) {
            return kicker.eligiblePeople;
          }

          // Fall back to legacy format - convert to new format
          const result: KickerEligiblePerson[] = [];

          // Handle assignedSpecialistIds (array) or assignedSpecialistId (single, deprecated)
          const specialistIds = kicker.assignedSpecialistIds ||
            (kicker.assignedSpecialistId ? [kicker.assignedSpecialistId] : []);

          specialistIds.forEach(specialistId => {
            const specialist = specialists.find(s => s.id === specialistId);
            if (specialist) {
              // Determine the payee name - use assignedOwner if set, otherwise specialist name
              const payeeName = specialist.assignedOwner || specialist.name;
              const isOwner = STRATEGY_PARTNERS.includes(payeeName);

              result.push({
                name: payeeName,
                type: isOwner ? 'owner' : 'external',
                role: specialist.name
              });
            }
          });

          // Add operations owners if eligible
          if (kicker.operationsOwnersEligible) {
            operationsOwners.forEach(owner => {
              // Only add if not already in the list
              if (!result.some(p => p.name === owner)) {
                result.push({
                  name: owner,
                  type: STRATEGY_PARTNERS.includes(owner) ? 'owner' : 'external',
                  role: 'Operations'
                });
              }
            });
          }

          return result;
        };

        baseTemplateData.performanceKickers.forEach(kicker => {
          const tier = KICKER_TIERS.find(t => t.value === kicker.selectedTier);
          if (tier && tier.percentage > 0) {
            const kickerRevenue = kicker.marketingSpend * tier.percentage;
            projectKickerRevenue += kickerRevenue;

            // Get eligible people using new model (with fallback to legacy)
            const eligiblePeople = resolveKickerEligiblePeople(
              kicker,
              baseTemplateData.tacticalSpecialists,
              baseTemplateData.operationsOwners
            );

            // Calculate share percentage (new field name with fallback to legacy)
            const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;
            const payoutPerPerson = kickerRevenue * (sharePercentage / 100);

            // Calculate total payouts to eligible people
            const totalEligiblePayouts = payoutPerPerson * eligiblePeople.length;
            projectKickerCosts += totalEligiblePayouts;

            // Distribute payouts to eligible people (track separately for UI)
            eligiblePeople.forEach(person => {
              if (person.type === 'owner' && ownerKickerEarnings[person.name] !== undefined) {
                ownerKickerEarnings[person.name] += payoutPerPerson;
                ownerKickerSpecialistEarnings[person.name] += payoutPerPerson;
              }
              // External payouts are tracked at the project kicker cost level
            });

            // Calculate remaining amount for owner share and distribute equally
            const ownersPayout = kickerRevenue - totalEligiblePayouts;
            const payoutPerOwner = ownersPayout / STRATEGY_PARTNERS.length;
            STRATEGY_PARTNERS.forEach(owner => {
              ownerKickerEarnings[owner] += payoutPerOwner;
              ownerKickerOwnerShare[owner] += payoutPerOwner;
            });

            const eligibleNames = eligiblePeople.map(p => `${p.name}(${p.type})`).join(', ');
            console.log(`  Kicker: ${kicker.name} - Revenue: ${kickerRevenue}, Share %: ${sharePercentage}%, Eligible: [${eligibleNames}], Payout per person: ${payoutPerPerson}, Total eligible payouts: ${totalEligiblePayouts}, Owner share: ${ownersPayout}`);
          }
        });
        
        console.log(`Total Kicker revenue: ${projectKickerRevenue}`);
        console.log(`Kicker costs: ${projectKickerCosts}`);
      }

      // 5. Calculate total revenue and costs
      const totalRevenue = revenue + projectKickerRevenue;
      const totalProjectCosts = operationsCost + tacticalSpecialistsCosts + projectKickerCosts;
      
      // 6. Calculate owner distribution FIRST (margin taken from gross revenue before costs)
      const baseOwnerDistribution = revenue * profitMarginDecimal;
      // Kicker owner share = kicker revenue MINUS specialist payouts
      const kickerOwnerShare = projectKickerRevenue - projectKickerCosts;
      const totalOwnerDistribution = baseOwnerDistribution + kickerOwnerShare;
      
      // 7. Calculate remaining operations budget
      const remainingBudget = totalRevenue - totalOwnerDistribution - totalProjectCosts;

      console.log(`=== PROJECT CALCULATION BREAKDOWN ===`);
      console.log(`Base Revenue: ${revenue}`);
      console.log(`Kicker Revenue: ${projectKickerRevenue}`);
      console.log(`Total Revenue: ${totalRevenue}`);
      console.log(`Owner Distribution (${baseTemplateData.profitMargin}% of base revenue): ${baseOwnerDistribution}`);
      console.log(`Kicker Owner Share: ${kickerOwnerShare}`);
      console.log(`Total Owner Distribution: ${totalOwnerDistribution}`);
      console.log(`Operations Cost: ${operationsCost} (${baseTemplateData.operationsOverheadPercentage}% of revenue + ${totalOperationsCompensation} compensation)`);
      console.log(`Tactical Specialists: ${tacticalSpecialistsCosts}`);
      console.log(`Kicker Costs: ${projectKickerCosts}`);
      console.log(`Total Operating Costs: ${totalProjectCosts}`);
      console.log(`Remaining Budget: ${remainingBudget}`);
      console.log(`====================================`);

      // 8. Accumulate totals
      totalMonthlyRevenue += totalRevenue;
      totalOperatingCosts += totalProjectCosts;
      totalProfit += baseOwnerDistribution; // FIXED: Only count base profit share, not kicker owner share
      totalKickerRevenue += projectKickerRevenue;
      
      // Accumulate cost breakdowns
      totalTacticalCosts += tacticalSpecialistsCosts;
      totalOperationsPercentageCosts += baseOperationsCost;
      totalOperationsCompensationCosts += totalOperationsCompensation;
      totalKickerCosts += projectKickerCosts;

      // 9. Distribute owner distribution equally among active owners (excluding disabled ones)
      const activeOwners = STRATEGY_PARTNERS.filter(owner => !disabledOwners.includes(owner));
      const profitPerOwner = activeOwners.length > 0 ? totalOwnerDistribution / activeOwners.length : 0;
      
      // Break down what each owner gets - ONLY BASE PROFIT SHARE in ownerDistributions
      const basePerOwner = activeOwners.length > 0 ? baseOwnerDistribution / activeOwners.length : 0;
      const kickerPerOwner = activeOwners.length > 0 ? kickerOwnerShare / activeOwners.length : 0;
      
      // FIXED: Only add base profit share to ownerDistributions
      // NOTE: Kicker owner share is already distributed in the kicker loop above (lines 184-188)
      // Do NOT add kickerPerOwner here - it would double-count!
      activeOwners.forEach(owner => {
        ownerDistributions[owner] += basePerOwner;
      });
      
      if (disabledOwners.length > 0) {
        console.log(`Owner redistribution active: ${disabledOwners.join(', ')} excluded from profit distributions. Their share is redistributed equally among ${activeOwners.length} active owners.`);
        console.log(`Per owner: Base ${formatCurrency(basePerOwner)} + Kicker ${formatCurrency(kickerPerOwner)} = Total ${formatCurrency(profitPerOwner)}`);
      }

      // 10. Calculate tactical earnings for owners (BASE TEMPLATE AMOUNTS ONLY)
      baseTemplateData.tacticalSpecialists.forEach(specialist => {
        if (specialist.assignedOwner && STRATEGY_PARTNERS.includes(specialist.assignedOwner)) {
          ownerTacticalEarnings[specialist.assignedOwner] += specialist.amount;
        }
      });

      // 11. Calculate operations earnings for owners (BASE TEMPLATE ASSIGNMENTS ONLY)
      if (baseTemplateData.operationsOwners.length > 0) {
        baseTemplateData.operationsOwners.forEach(owner => {
          if (STRATEGY_PARTNERS.includes(owner)) {
            ownerOperationsEarnings[owner] += operationsCompensationPerOwner;
          }
        });
      }
    });

    console.log('=== COMPANY SUMMARY FINAL TOTALS ===');
    console.log('Total Monthly Revenue:', totalMonthlyRevenue);
    console.log('Total Kicker Revenue:', totalKickerRevenue);
    console.log('Total Base Profit (margin % from base revenue only):', totalProfit);
    console.log('Total Operating Costs:', totalOperatingCosts);
    console.log('  - Tactical Specialists:', totalTacticalCosts);
    console.log('  - Operations % Costs:', totalOperationsPercentageCosts);
    console.log('  - Operations Compensation:', totalOperationsCompensationCosts);
    console.log('  - Kicker Costs:', totalKickerCosts);
    console.log('Owner Distributions (base profit share only):', ownerDistributions);
    console.log('Owner Tactical Earnings:', ownerTacticalEarnings);
    console.log('Owner Operations Earnings:', ownerOperationsEarnings);
    console.log('Owner Kicker Earnings (includes specialist shares + owner distribution):', ownerKickerEarnings);
    console.log('===================================');

    return {
      totalMonthlyRevenue,
      totalProfit,
      totalOperatingCosts,
      totalKickerRevenue,
      ownerDistributions,
      ownerTacticalEarnings,
      ownerOperationsEarnings,
      ownerKickerEarnings,
      ownerKickerSpecialistEarnings,
      ownerKickerOwnerShare,
      projectCount: projectsToInclude.filter(p => {
        const migrated = migrateProjectToTemplate(p);
        return migrated.baseTemplate !== undefined;
      }).length,
      totalTacticalCosts,
      totalOperationsPercentageCosts,
      totalOperationsCompensationCosts,
      totalKickerCosts,
    };
  };

  const companySummary = calculateCompanySummary();

  // Filter projects for display
  const filteredProjects = projects.filter(project => {
    if (statusFilter === 'all') return true;
    return project.status === statusFilter;
  });

  const getStatusBadgeProps = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return { variant: 'success' as const };
      case 'inactive':
        return { variant: 'secondary' as const };
      case 'prospective':
        return { variant: 'info' as const };
      default:
        return { variant: 'secondary' as const };
    }
  };

  const activeCount = projects.filter(p => p.status === 'active').length;
  const inactiveCount = projects.filter(p => p.status === 'inactive').length;
  const prospectiveCount = projects.filter(p => p.status === 'prospective').length;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Project Portfolio</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {activeCount} active • {prospectiveCount} prospective • {inactiveCount} inactive
          </p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(companySummary.totalMonthlyRevenue)} {includeProspective ? 'potential' : 'guaranteed'} monthly
          </p>
        </div>

        {/* Row 2: Filter + Secondary Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground hidden sm:block" />
            <Select value={statusFilter} onValueChange={(value: ProjectStatus | 'all') => setStatusFilter(value)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="active">Active ({activeCount})</SelectItem>
                <SelectItem value="prospective">Prospective ({prospectiveCount})</SelectItem>
                <SelectItem value="inactive">Inactive ({inactiveCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons - grid on mobile, flex on desktop */}
          <div className="grid grid-cols-2 sm:flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportAndDownloadProjects(projects)}
              title="Export active projects to CSV"
              className="text-xs sm:text-sm"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            {onViewPayments && (
              <Button
                variant="outline"
                size="sm"
                onClick={onViewPayments}
                className="text-xs sm:text-sm"
              >
                <History className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompanySummary(!showCompanySummary)}
              className="text-xs sm:text-sm"
            >
              <BarChart3 className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{showCompanySummary ? 'Hide' : 'Show'}</span>
            </Button>
            {canMarkAsPaid && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowMarkAsPaidDialog(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs sm:text-sm"
              >
                <CheckCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Mark Paid</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onSelectProject(null)}
              className="bg-accent hover:bg-accent/90 text-accent-foreground col-span-2 sm:col-span-1 text-xs sm:text-sm"
            >
              <Plus className="h-4 w-4 mr-1 sm:mr-2" />
              New Project
            </Button>
          </div>
        </div>
      </div>

      {/* Company Summary */}
      {showCompanySummary && (
        <Card>
          <CardHeader className="px-3 sm:px-6">
            <div className="flex flex-col gap-4">
              <div>
                <CardTitle className="text-primary text-base sm:text-lg">Company Financial Summary</CardTitle>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Shows {includeProspective ? 'potential' : 'guaranteed'} revenue{includeProjectedKickers ? ' including kickers' : ''}. View Historical Dashboard for actual performance.
                </p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    id="include-prospective"
                    checked={includeProspective}
                    onCheckedChange={setIncludeProspective}
                  />
                  <label htmlFor="include-prospective" className="text-xs sm:text-sm font-medium cursor-pointer" title="Include CRM prospects in pipeline stages (intro, meeting, pitched, trial/audit) to see projected MRR">
                    Include CRM Pipeline
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="include-projected-kickers"
                    checked={includeProjectedKickers}
                    onCheckedChange={setIncludeProjectedKickers}
                  />
                  <label htmlFor="include-projected-kickers" className="text-xs sm:text-sm font-medium">
                    Include Kickers
                  </label>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {/* Weighted Pipeline Forecast */}
            {pipelineClients.length > 0 && (
              <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Revenue Forecast
                  </h4>
                  <Badge variant="outline" className="text-xs">
                    {pipelineClients.length} clients
                  </Badge>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Active MRR */}
                  <div className="p-3 bg-card rounded-lg border">
                    <div className="text-xs text-muted-foreground mb-1">Active MRR</div>
                    <div className="text-lg font-bold text-foreground font-serif">
                      {formatCurrency(pipelineMetrics.activeRevenue)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {pipelineMetrics.activeCount} active
                    </div>
                  </div>
                  {/* Committed */}
                  <div className="p-3 bg-card rounded-lg border">
                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      Committed
                    </div>
                    <div className="text-lg font-bold text-foreground font-serif">
                      +{formatCurrency(pipelineMetrics.committedRevenue)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {pipelineMetrics.committedCount} starting soon
                    </div>
                  </div>
                  {/* Pipeline */}
                  <div className="p-3 bg-accent/5 rounded-lg border border-accent/20">
                    <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Pipeline
                    </div>
                    <div className="text-lg font-bold text-foreground font-serif">
                      +{formatCurrency(pipelineMetrics.pipelineTotal)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {pipelineMetrics.pipelineCount} prospects
                    </div>
                  </div>
                  {/* Total Forecast */}
                  <div className="p-3 bg-accent/10 rounded-lg border border-accent/30">
                    <div className="text-xs text-muted-foreground mb-1">Total Forecast</div>
                    <div className="text-lg font-bold text-foreground font-serif">
                      {formatCurrency(pipelineMetrics.totalForecast)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      all stages combined
                    </div>
                  </div>
                  {/* One-Off — reported separately, never part of MRR */}
                  {pipelineMetrics.oneOffCount > 0 && (
                    <div className="p-3 bg-card rounded-lg border border-dashed">
                      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        One-Off
                      </div>
                      <div className="text-lg font-bold text-foreground font-serif">
                        {formatCurrency(pipelineMetrics.oneOffTotal)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {pipelineMetrics.oneOffCount} deal{pipelineMetrics.oneOffCount !== 1 ? 's' : ''} — not in MRR
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Math Verification Section */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-muted/50 rounded-lg border-2 border-border">
              <h4 className="text-sm sm:text-base font-semibold mb-3 text-center">Monthly Financial Calculation</h4>
              <div className="space-y-2 text-xs sm:text-sm max-w-2xl mx-auto">
                <div className="flex justify-between items-center gap-2">
                  <span className="font-medium">Total Gross Revenue:</span>
                  <span className="font-bold text-sm sm:text-lg font-serif">{formatCurrency(companySummary.totalMonthlyRevenue)}</span>
                </div>
                <div className="flex justify-between items-center gap-2 text-primary">
                  <span className="pl-2 sm:pl-4">− Profit Share:</span>
                  <span className="font-bold font-serif">({formatCurrency(companySummary.totalProfit)})</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between items-center gap-2">
                  <span className="font-medium">= Ops Budget:</span>
                  <span className="font-bold text-sm sm:text-lg font-serif">{formatCurrency(companySummary.totalMonthlyRevenue - companySummary.totalProfit)}</span>
                </div>
                <div className="flex justify-between items-center gap-2 text-negative">
                  <span className="pl-2 sm:pl-4">− Operating Costs:</span>
                  <span className="font-bold">({formatCurrency(companySummary.totalOperatingCosts)})</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between items-center gap-2 bg-muted p-2 rounded">
                  <span className="font-bold">= Remaining:</span>
                  <span className={`font-bold text-base sm:text-xl font-serif ${(companySummary.totalMonthlyRevenue - companySummary.totalProfit - companySummary.totalOperatingCosts) >= 0 ? 'text-positive' : 'text-negative'}`}>{formatCurrency(companySummary.totalMonthlyRevenue - companySummary.totalProfit - companySummary.totalOperatingCosts)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardDescription className="text-xs">
                    {includeProjectedKickers ? 'Total Revenue' : 'Base Revenue'}
                  </CardDescription>
                  <CardTitle className="text-lg sm:text-2xl font-semibold tabular-nums font-serif">
                    {formatCurrency(companySummary.totalMonthlyRevenue)}
                  </CardTitle>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1 text-sm pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                  <div className="text-muted-foreground text-[10px] sm:text-xs hidden sm:block">
                    {includeProjectedKickers ? 'Includes kickers' : 'Retainers only'}
                  </div>
                  {includeProjectedKickers && companySummary.totalKickerRevenue > 0 && (
                    <div className="text-[10px] sm:text-xs text-accent">
                      +{formatCurrency(companySummary.totalKickerRevenue)} kickers
                    </div>
                  )}
                </CardFooter>
              </Card>
              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardDescription className="text-xs">Profit Share</CardDescription>
                  <CardTitle className="text-lg sm:text-2xl font-semibold tabular-nums text-primary font-serif">
                    {formatCurrency(companySummary.totalProfit)}
                  </CardTitle>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1 text-sm pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                  <div className="text-muted-foreground text-[10px] sm:text-xs hidden sm:block">
                    Margin % from base
                  </div>
                </CardFooter>
              </Card>
              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardDescription className="text-xs">Operating Costs</CardDescription>
                  <CardTitle className="text-lg sm:text-2xl font-semibold tabular-nums text-negative font-serif">
                    {formatCurrency(companySummary.totalOperatingCosts)}
                  </CardTitle>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1 text-sm pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                  <div className="text-muted-foreground text-[10px] sm:text-xs hidden sm:block">
                    {includeProjectedKickers ? 'With payouts' : 'Base costs'}
                  </div>
                </CardFooter>
              </Card>
              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardDescription className="text-xs">Projects</CardDescription>
                  <CardTitle className="text-lg sm:text-2xl font-semibold tabular-nums font-serif">
                    {companySummary.projectCount}
                  </CardTitle>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1 text-sm pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                  <div className="text-muted-foreground text-[10px] sm:text-xs hidden sm:block">
                    Currently tracked
                  </div>
                </CardFooter>
              </Card>
            </div>

            <Separator className="my-4" />

            {/* Operating Costs Breakdown */}
            <div className="mb-4 sm:mb-6">
              <h4 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">Operating Costs Breakdown</h4>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 border rounded-lg bg-[#4A6FA5]/8">
                  <div className="text-[10px] sm:text-xs text-muted-foreground mb-1">Ops Overhead %</div>
                  <div className="text-sm sm:text-lg font-bold text-[#4A6FA5]">{formatCurrency(companySummary.totalOperationsPercentageCosts)}</div>
                </div>
                <div className="p-2 sm:p-3 border rounded-lg bg-[#4A6FA5]/8">
                  <div className="text-[10px] sm:text-xs text-muted-foreground mb-1">Ops Compensation</div>
                  <div className="text-sm sm:text-lg font-bold text-[#4A6FA5]">{formatCurrency(companySummary.totalOperationsCompensationCosts)}</div>
                </div>
                <div className="p-2 sm:p-3 border rounded-lg bg-[#2A8F82]/8">
                  <div className="text-[10px] sm:text-xs text-muted-foreground mb-1">Tactical</div>
                  <div className="text-sm sm:text-lg font-bold text-[#2A8F82]">{formatCurrency(companySummary.totalTacticalCosts)}</div>
                </div>
                {includeProjectedKickers && companySummary.totalKickerCosts > 0 && (
                  <div className="p-2 sm:p-3 border rounded-lg bg-accent/10">
                    <div className="text-[10px] sm:text-xs text-muted-foreground mb-1">Kicker Payouts</div>
                    <div className="text-sm sm:text-lg font-bold text-accent">{formatCurrency(companySummary.totalKickerCosts)}</div>
                  </div>
                )}
              </div>
              <div className="mt-2 text-center hidden sm:block">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Total: {formatCurrency(companySummary.totalOperatingCosts)} = Overhead + Comp + Tactical{includeProjectedKickers && companySummary.totalKickerCosts > 0 ? ' + Kickers' : ''}
                </p>
              </div>
            </div>

            <Separator className="my-4" />

            {/* Team Compensation Breakdown */}
            <div>
              <h4 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">
                {includeProjectedKickers ? 'Projected' : 'Base'} Team Compensation
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {(() => {
                  // Build comprehensive list of all people with compensation
                  const peopleCompensation: Record<string, {
                    profitShare: number;
                    projectRoles: Array<{
                      project: string;
                      projectId: string;
                      role: string;
                      amount: number;
                      type: 'operations' | 'tactical' | 'overhead'
                    }>;
                    kickerEarnings: number;
                    kickerSpecialistEarnings: number; // Specialist bonus (only for people who worked on project)
                    kickerOwnerShare: number; // Owner share (split among all 5 owners)
                    total: number;
                  }> = {};

                  // Initialize owners with profit share
                  STRATEGY_PARTNERS.forEach(owner => {
                    const profitShare = companySummary.ownerDistributions[owner] || 0;
                    const kickerEarnings = includeProjectedKickers ? (companySummary.ownerKickerEarnings[owner] || 0) : 0;
                    const kickerSpecialistEarnings = includeProjectedKickers ? (companySummary.ownerKickerSpecialistEarnings[owner] || 0) : 0;
                    const kickerOwnerShare = includeProjectedKickers ? (companySummary.ownerKickerOwnerShare[owner] || 0) : 0;

                    peopleCompensation[owner] = {
                      profitShare: profitShare,
                      projectRoles: [],
                      kickerEarnings: kickerEarnings,
                      kickerSpecialistEarnings: kickerSpecialistEarnings,
                      kickerOwnerShare: kickerOwnerShare,
                      total: profitShare + kickerEarnings
                    };
                  });

                  // Add project-level assignments for each person
                  const projectsToInclude = projects.filter(project => {
                    if (project.status === 'inactive') return false;
                    if (project.revenueType === 'variable' && (!project.adHocForecasts || project.adHocForecasts.length === 0)) return false;
                    if (project.status === 'prospective') return includeProspective;
                    return true;
                  });

                  projectsToInclude.forEach(project => {
                    const migratedProject = migrateProjectToTemplate(project);
                    if (!migratedProject.baseTemplate) return;

                    const base = migratedProject.baseTemplate;
                    const projectName = project.projectName || 'Unnamed Project';

                    // Add operations assignments (per project, per owner)
                    base.operationsOwners.forEach(owner => {
                      if (!peopleCompensation[owner]) {
                        peopleCompensation[owner] = {
                          profitShare: 0,
                          projectRoles: [],
                          kickerEarnings: 0,
                          kickerSpecialistEarnings: 0,
                          kickerOwnerShare: 0,
                          total: 0
                        };
                      }

                      const amount = base.operationsCompensationPerOwner || 2500;
                      peopleCompensation[owner].projectRoles.push({
                        project: projectName,
                        projectId: project.id,
                        role: 'Operations Manager',
                        amount: amount,
                        type: 'operations'
                      });
                      peopleCompensation[owner].total += amount;
                    });

                    // Add tactical specialist assignments (per project)
                    base.tacticalSpecialists.forEach(specialist => {
                      if (specialist.assignedOwner) {
                        const person = specialist.assignedOwner;
                        if (!peopleCompensation[person]) {
                          peopleCompensation[person] = {
                            profitShare: 0,
                            projectRoles: [],
                            kickerEarnings: 0,
                            kickerSpecialistEarnings: 0,
                            kickerOwnerShare: 0,
                            total: 0
                          };
                        }

                        peopleCompensation[person].projectRoles.push({
                          project: projectName,
                          projectId: project.id,
                          role: specialist.name,
                          amount: specialist.amount,
                          type: 'tactical'
                        });
                        peopleCompensation[person].total += specialist.amount;
                      }
                    });
                  });

                  // Sort people by total compensation (highest first)
                  const sortedPeople = Object.entries(peopleCompensation)
                    .filter(([, comp]) => comp.total > 0)
                    .sort(([, a], [, b]) => b.total - a.total);

                  return sortedPeople.map(([person, comp]) => {
                    const isOwner = STRATEGY_PARTNERS.includes(person);
                    const isDisabled = disabledOwners.includes(person);
                    
                    return (
                      <div
                        key={person}
                        className={`p-4 border-2 rounded-lg bg-gradient-to-br transition-shadow ${
                          isDisabled
                            ? 'from-muted to-muted/80 border-border opacity-75'
                            : 'from-card to-card/80 border-border hover:shadow-md'
                        }`}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h5 className="font-semibold">{person}</h5>
                            {isOwner && (
                              <Badge variant="default" className="bg-primary text-primary-foreground text-xs h-5">
                                Collective
                              </Badge>
                            )}
                            {isDisabled && (
                              <Badge variant="outline" className="text-xs h-5 border-warning/50 text-warning">
                                No Profit Share
                              </Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-foreground font-serif">{formatCurrency(comp.total)}</div>
                          </div>
                        </div>

                        {/* Compensation Breakdown */}
                        <div className="space-y-1.5 text-xs">
                          {/* Profit Share (Owners Only) */}
                          {comp.profitShare > 0 && (
                            <div className="flex items-center justify-between py-1 px-2 bg-card/60 rounded">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                <div className="flex flex-col">
                                  <span className="font-medium">Base Profit Share</span>
                                  <span className="text-[9px] text-muted-foreground leading-tight">
                                    From margin % of base revenue
                                  </span>
                                </div>
                              </div>
                              <span className="font-semibold text-primary">{formatCurrency(comp.profitShare)}</span>
                            </div>
                          )}
                          
                          {/* Show message if owner is disabled */}
                          {isOwner && isDisabled && (
                            <div className="flex items-center justify-between py-1 px-2 bg-warning/10 rounded border border-warning/30">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-warning"></div>
                                <span className="font-medium text-warning">Profit Share Disabled</span>
                              </div>
                              <span className="text-[10px] text-warning/80">Redistributed to active owners</span>
                            </div>
                          )}

                          {/* Project Roles - Grouped by type */}
                          {comp.projectRoles.map((role, idx) => {
                            const colorClass = role.type === 'operations'
                              ? 'text-[#4A6FA5]'
                              : role.type === 'tactical'
                              ? 'text-[#2A8F82]'
                              : 'text-accent';
                            const dotColor = role.type === 'operations'
                              ? 'bg-[#4A6FA5]'
                              : role.type === 'tactical'
                              ? 'bg-[#2A8F82]'
                              : 'bg-accent';
                            
                            return (
                              <div key={idx} className="flex items-start justify-between py-1 px-2 bg-card/60 rounded">
                                <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                  <div className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-1 flex-shrink-0`}></div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-muted-foreground text-[10px] leading-tight truncate">{role.project}</div>
                                    <div className="font-medium leading-tight">{role.role}</div>
                                  </div>
                                </div>
                                <span className={`font-semibold ${colorClass} ml-2 flex-shrink-0`}>
                                  {formatCurrency(role.amount)}
                                </span>
                              </div>
                            );
                          })}

                          {/* Kicker Earnings - Specialist Bonus (only for people who worked on project) */}
                          {includeProjectedKickers && comp.kickerSpecialistEarnings > 0 && (
                            <div className="flex items-center justify-between py-1 px-2 bg-card/60 rounded">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                                <div className="flex flex-col">
                                  <span className="font-medium">Specialist Bonus</span>
                                  <span className="text-[9px] text-muted-foreground leading-tight">
                                    10% of kicker per project worked
                                  </span>
                                </div>
                              </div>
                              <span className="font-semibold text-accent">{formatCurrency(comp.kickerSpecialistEarnings)}</span>
                            </div>
                          )}

                          {/* Kicker Earnings - Owner Share (split among all 5 owners) */}
                          {includeProjectedKickers && comp.kickerOwnerShare > 0 && (
                            <div className="flex items-center justify-between py-1 px-2 bg-card/60 rounded">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-positive"></div>
                                <div className="flex flex-col">
                                  <span className="font-medium">Collective Bonus</span>
                                  <span className="text-[9px] text-muted-foreground leading-tight">
                                    Share of kicker remainder (÷5 owners)
                                  </span>
                                </div>
                              </div>
                              <span className="font-semibold text-positive">{formatCurrency(comp.kickerOwnerShare)}</span>
                            </div>
                          )}
                        </div>

                        {/* Role Summary Footer */}
                        <div className="mt-2 pt-2 border-t border-border text-[10px] text-muted-foreground">
                          {comp.profitShare > 0 && 'Owner Share'}
                          {comp.profitShare > 0 && comp.projectRoles.length > 0 && ' • '}
                          {comp.projectRoles.length > 0 && `${comp.projectRoles.length} Project Role${comp.projectRoles.length > 1 ? 's' : ''}`}
                          {includeProjectedKickers && comp.kickerEarnings > 0 && ' • Bonuses'}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              
              {/* Summary Footer */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      {includeProjectedKickers ? 'Projected total' : 'Guaranteed'} monthly team compensation
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {(() => {
                        const peopleSet = new Set<string>();
                        STRATEGY_PARTNERS.forEach(o => peopleSet.add(o));
                        
                        projects.filter(p => {
                          if (p.status === 'inactive') return false;
                          if (p.status === 'prospective') return includeProspective;
                          return true;
                        }).forEach(project => {
                          const migratedProject = migrateProjectToTemplate(project);
                          if (!migratedProject.baseTemplate) return;
                          const base = migratedProject.baseTemplate;
                          
                          base.operationsOwners.forEach(o => peopleSet.add(o));
                          base.tacticalSpecialists.forEach(s => s.assignedOwner && peopleSet.add(s.assignedOwner));
                        });
                        
                        return `${peopleSet.size} team members with active roles`;
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary font-serif">
                      {formatCurrency(
                        // Calculate actual compensation from what individuals receive
                        Object.values(companySummary.ownerDistributions).reduce((a, b) => a + b, 0) +
                        Object.values(companySummary.ownerOperationsEarnings).reduce((a, b) => a + b, 0) +
                        Object.values(companySummary.ownerTacticalEarnings).reduce((a, b) => a + b, 0) +
                        (includeProjectedKickers ? Object.values(companySummary.ownerKickerEarnings).reduce((a, b) => a + b, 0) : 0)
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">total compensation</div>
                  </div>
                </div>
                
                {/* Profit Redistribution Notice */}
                {disabledOwners.length > 0 && (
                  <div className="mt-3 p-3 bg-warning/10 rounded-lg border border-warning/30">
                    <div className="flex items-start gap-2">
                      <Settings className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <span className="font-medium text-warning">Profit redistribution active:</span>
                        <span className="text-warning/80 ml-1">
                          {disabledOwners.join(', ')} excluded from profit distributions. 
                          Their share is redistributed equally among {STRATEGY_PARTNERS.length - disabledOwners.length} active owner{STRATEGY_PARTNERS.length - disabledOwners.length !== 1 ? 's' : ''}.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Per-Project Breakdown Table */}
            <div className="mb-4 sm:mb-6">
              <h4 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">Per-Project Financial Breakdown</h4>
              <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                <table className="w-full text-xs sm:text-sm border min-w-[600px]">
                  <thead className="bg-muted">
                    <tr>
                      <th className="border p-1.5 sm:p-2 text-left">Project</th>
                      <th className="border p-1.5 sm:p-2 text-right">Revenue</th>
                      <th className="border p-1.5 sm:p-2 text-right">Margin %</th>
                      <th className="border p-1.5 sm:p-2 text-right">Owner Dist</th>
                      <th className="border p-1.5 sm:p-2 text-right">Costs</th>
                      <th className="border p-1.5 sm:p-2 text-right">Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const projectsToInclude = projects.filter(project => {
                        if (project.status === 'inactive') return false;
                        if (project.revenueType === 'variable' && (!project.adHocForecasts || project.adHocForecasts.length === 0)) return false;
                        if (project.status === 'prospective') return includeProspective;
                        return true;
                      });
                      
                      return projectsToInclude.map(project => {
                        const migratedProject = migrateProjectToTemplate(project);
                        if (!migratedProject.baseTemplate) return null;
                        
                        const base = migratedProject.baseTemplate;
                        const revenue = getProjectRevenue(project, base.revenue);
                        const profitMarginDecimal = base.profitMargin / 100;
                        
                        // Calculate costs
                        const operationsCompensationPerOwner = base.operationsCompensationPerOwner || 2500;
                        const totalOperationsCompensation = base.operationsOwners.length * operationsCompensationPerOwner;
                        const baseOperationsCost = revenue * (base.operationsOverheadPercentage / 100);
                        const operationsCost = baseOperationsCost + totalOperationsCompensation;
                        const tacticalCosts = base.tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);
                        
                        // Kickers (if enabled)
                        let kickerRevenue = 0;
                        let kickerCosts = 0;
                        if (includeProjectedKickers) {
                          const KICKER_TIERS = [
                            { value: 'none', percentage: 0 },
                            { value: 'tier1', percentage: 0.05 },
                            { value: 'tier2', percentage: 0.075 },
                            { value: 'tier3', percentage: 0.10 },
                          ];
                          base.performanceKickers?.forEach(kicker => {
                            const tier = KICKER_TIERS.find(t => t.value === kicker.selectedTier);
                            if (tier && tier.percentage > 0) {
                              const kr = kicker.marketingSpend * tier.percentage;
                              kickerRevenue += kr;

                              // Calculate eligible people count using new model (with fallback)
                              let eligibleCount = 0;
                              if (kicker.eligiblePeople && kicker.eligiblePeople.length > 0) {
                                // New model - use eligiblePeople directly
                                eligibleCount = kicker.eligiblePeople.length;
                              } else {
                                // Legacy model - count from IDs and flags
                                const assignedSpecialistIds = kicker.assignedSpecialistIds || [];
                                assignedSpecialistIds.forEach(specialistId => {
                                  const specialist = base.tacticalSpecialists.find(s => s.id === specialistId);
                                  if (specialist) {
                                    eligibleCount++;
                                  }
                                });
                                if (kicker.operationsOwnersEligible) {
                                  base.operationsOwners.forEach(owner => {
                                    // Avoid double-counting if owner is also assigned to a specialist
                                    const alreadyCounted = assignedSpecialistIds.some(id => {
                                      const spec = base.tacticalSpecialists.find(s => s.id === id);
                                      return spec?.assignedOwner === owner;
                                    });
                                    if (!alreadyCounted) {
                                      eligibleCount++;
                                    }
                                  });
                                }
                              }

                              // Calculate costs using new field name with fallback
                              const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;
                              kickerCosts += kr * (sharePercentage / 100) * eligibleCount;
                            }
                          });
                        }
                        
                        const totalRevenue = revenue + kickerRevenue;
                        const totalCosts = operationsCost + tacticalCosts + kickerCosts;
                        
                        // Calculate owner distribution FIRST (margin taken from gross revenue)
                        const baseOwnerDistribution = revenue * profitMarginDecimal;
                        // Kicker owner share = kicker revenue MINUS specialist payouts
                        const kickerOwnerShare = kickerRevenue - kickerCosts;
                        const ownerDistribution = baseOwnerDistribution + kickerOwnerShare;
                        
                        // Calculate remaining budget
                        const remainingBudget = totalRevenue - ownerDistribution - totalCosts;
                        
                        return (
                          <tr key={project.id} className="hover:bg-muted/50">
                            <td className="border p-2">
                              {project.projectName}
                              <div className="text-xs text-muted-foreground">
                                Status: {project.status}
                              </div>
                            </td>
                            <td className="border p-2 text-right font-medium">
                              {formatCurrency(totalRevenue)}
                              {kickerRevenue > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  Base: {formatCurrency(revenue)} + Kickers: {formatCurrency(kickerRevenue)}
                                </div>
                              )}
                            </td>
                            <td className="border p-2 text-right font-medium">
                              {base.profitMargin}%
                            </td>
                            <td className="border p-2 text-right font-bold text-positive">
                              {formatCurrency(ownerDistribution)}
                              <div className="text-xs text-muted-foreground">
                                Base: {formatCurrency(baseOwnerDistribution)}
                                {kickerOwnerShare > 0 && (
                                  <> | Kicker: {formatCurrency(kickerOwnerShare)}</>
                                )}
                              </div>
                            </td>
                            <td className="border p-2 text-right text-negative">
                              {formatCurrency(totalCosts)}
                              <div className="text-xs text-muted-foreground">
                                Ops: {formatCurrency(operationsCost)} | Tac: {formatCurrency(tacticalCosts)}
                                {kickerCosts > 0 && (
                                  <> | Kicker Specialists: {formatCurrency(kickerCosts)}</>
                                )}
                              </div>
                            </td>
                            <td className={`border p-2 text-right font-medium ${remainingBudget >= 0 ? 'text-positive' : 'text-negative'}`}>
                              {formatCurrency(remainingBudget)}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                    <tr className="bg-muted font-bold">
                      <td className="border p-2">TOTAL</td>
                      <td className="border p-2 text-right">{formatCurrency(companySummary.totalMonthlyRevenue)}</td>
                      <td className="border p-2 text-right">
                        {((companySummary.totalProfit / companySummary.totalMonthlyRevenue) * 100).toFixed(1)}%
                      </td>
                      <td className="border p-2 text-right text-positive">{formatCurrency(companySummary.totalProfit)}</td>
                      <td className="border p-2 text-right text-negative">{formatCurrency(companySummary.totalOperatingCosts)}</td>
                      <td className="border p-2 text-right">
                        {formatCurrency(companySummary.totalMonthlyRevenue - companySummary.totalProfit - companySummary.totalOperatingCosts)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Admin Settings - at very bottom (only visible to admins/owners) */}
            {canEditProjects && (
              <div className="mt-8 pt-4 border-t border-border">
                <button
                  onClick={() => setShowOwnerSettings(!showOwnerSettings)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Settings className="h-3 w-3" />
                  <span>Admin Settings</span>
                  {disabledOwners.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] h-4 bg-warning/15 text-warning border-warning/30">
                      {disabledOwners.length} excluded
                    </Badge>
                  )}
                  <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showOwnerSettings ? 'rotate-180' : ''}`} />
                </button>

                {showOwnerSettings && (
                  <div className="mt-3 p-3 bg-muted rounded border border-border text-sm">
                    <h5 className="text-xs font-medium mb-2 text-muted-foreground">Owner Profit Distribution</h5>
                    <p className="text-[10px] text-muted-foreground mb-3">
                      Enable/disable individual owners from receiving base profit share distributions. Disabled owners still receive operational compensation, tactical pay, and bonuses.
                    </p>
                    <div className="grid grid-cols-5 gap-2">
                      {STRATEGY_PARTNERS.map(owner => {
                        const isEnabled = !disabledOwners.includes(owner);
                        const toggleOwner = () => {
                          if (isEnabled) {
                            setDisabledOwners([...disabledOwners, owner]);
                          } else {
                            setDisabledOwners(disabledOwners.filter(o => o !== owner));
                          }
                        };

                        return (
                          <div
                            key={owner}
                            onClick={toggleOwner}
                            className={`p-2 rounded border cursor-pointer transition-all text-xs ${
                              isEnabled
                                ? 'bg-card border-primary/30 hover:border-primary/50'
                                : 'bg-muted border-border hover:border-border/80 opacity-60'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${isEnabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {owner}
                              </span>
                              <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-primary' : 'bg-muted-foreground'}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px]">
                      <span className="text-muted-foreground">
                        {STRATEGY_PARTNERS.length - disabledOwners.length}/{STRATEGY_PARTNERS.length} receiving distributions
                      </span>
                      {disabledOwners.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDisabledOwners([])}
                          className="h-5 text-[10px] px-2"
                        >
                          Enable All
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Project List */}
      {filteredProjects.length > 0 ? (
        <Card>
          <CardHeader className="px-3 sm:px-6">
            <CardTitle className="text-base sm:text-lg">
              Projects
              {statusFilter !== 'all' && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  • {filteredProjects.length} {statusFilter}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="space-y-3">
              {filteredProjects.map((project) => {
                const currentMonth = new Date().toISOString().substring(0, 7);
                const migratedProject = migrateProjectToTemplate(project);
                const baseTemplateData = migratedProject.baseTemplate;
                const monthlyData = migratedProject.baseTemplate ? resolveMonthlyData(migratedProject, currentMonth) : null;
                const availableMonths = Object.keys(migratedProject.monthlyOverrides || {}).sort().reverse();

                return (
                  <div
                    key={project.id}
                    className={`p-3 sm:p-4 border rounded-lg transition-colors ${
                      selectedProject?.id === project.id ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
                          <h4 className="font-medium text-sm sm:text-base">
                            {project.projectName || 'Unnamed Project'}
                          </h4>
                          <Badge {...getStatusBadgeProps(project.status || 'active')} className="text-xs">
                            {(project.status || 'active').charAt(0).toUpperCase() + (project.status || 'active').slice(1)}
                          </Badge>
                          {baseTemplateData ? (
                            <>
                              <Badge variant="secondary" className="text-xs">
                                {formatCurrency(baseTemplateData.revenue)}/mo
                              </Badge>
                              <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                                {baseTemplateData.profitMargin}% profit
                              </Badge>
                              {(() => {
                                const KICKER_TIERS = [
                                  { value: 'none', label: 'No Performance Bonus', percentage: 0, description: 'Below 5.0X ROAS' },
                                  { value: 'tier1', label: '5% Kicker', percentage: 0.05, description: '5.0-5.9X ROAS' },
                                  { value: 'tier2', label: '7.5% Kicker', percentage: 0.075, description: '6.0-6.9X ROAS' },
                                  { value: 'tier3', label: '10% Kicker', percentage: 0.10, description: '7.0X+ ROAS' },
                                ];
                                
                                const totalKickerRevenue = baseTemplateData.performanceKickers.reduce((sum, kicker) => {
                                  const tier = KICKER_TIERS.find(t => t.value === kicker.selectedTier);
                                  return sum + (tier ? kicker.marketingSpend * tier.percentage : 0);
                                }, 0);
                                
                                return totalKickerRevenue > 0 && (
                                  <Badge variant="outline" className="text-accent border-accent/40 text-xs hidden sm:inline-flex">
                                    +{formatCurrency(totalKickerRevenue)}
                                  </Badge>
                                );
                              })()}
                            </>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              No template data
                            </Badge>
                          )}
                        </div>

                        <div className="text-xs sm:text-sm text-muted-foreground mb-2 space-y-0.5 sm:space-y-0 sm:flex sm:gap-4">
                          {baseTemplateData ? (
                            <>
                              <div>
                                <span className="font-medium">Strategy:</span> {baseTemplateData.strategySponsor || 'N/A'}
                              </div>
                              <div>
                                <span className="font-medium">Ops:</span> {
                                  baseTemplateData.operationsOwners.length > 0
                                    ? baseTemplateData.operationsOwners.join(', ')
                                    : 'N/A'
                                }
                              </div>
                              <div className="hidden sm:block">
                                <span className="font-medium">Specialists:</span> {baseTemplateData.tacticalSpecialists.length}
                                {baseTemplateData.performanceKickers && baseTemplateData.performanceKickers.length > 0 && (
                                  <span className="text-accent ml-1">({baseTemplateData.performanceKickers.length} kickers)</span>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-destructive">
                              No base template data available
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted-foreground">
                          <div>
                            Last updated: {project.updatedAt.toLocaleDateString()}
                          </div>
                          {availableMonths.length > 0 && (
                            <div>
                              Historical data: {availableMonths.length} month{availableMonths.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectProject(project);
                          }}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        {canDeleteProjects && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProjectToDelete(project);
                            }}
                            className="text-destructive hover:text-destructive"
                            title="Delete project (Admin only)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <div className="text-muted-foreground mb-4">
              <Plus className="h-12 w-12 mx-auto mb-4 opacity-50" />
              {projects.length === 0 ? (
                <>
                  <h3>No projects yet</h3>
                  <p>Create your first project to get started with revenue modeling.</p>
                </>
              ) : (
                <>
                  <h3>No {statusFilter} projects</h3>
                  <p>No projects found with the current filter. Try changing the status filter or create a new project.</p>
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {projects.length > 0 && statusFilter !== 'all' && (
                <Button
                  variant="outline"
                  onClick={() => setStatusFilter('all')}
                >
                  Show All Projects
                </Button>
              )}
              <Button
                onClick={() => onSelectProject(null)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mark as Paid Dialog */}
      <MarkAsPaidDialog
        open={showMarkAsPaidDialog}
        onOpenChange={setShowMarkAsPaidDialog}
        month={currentMonth}
        projects={projects}
        companySummary={companySummary}
        onSuccess={onRefresh}
      />

      {/* Delete Project Dialog */}
      <DeleteProjectDialog
        open={projectToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setProjectToDelete(null);
        }}
        projectName={projectToDelete?.projectName || ''}
        onConfirm={async () => {
          if (projectToDelete) {
            await onDeleteProject(projectToDelete.id);
            setProjectToDelete(null);
          }
        }}
      />
    </div>
  );
}